import { ServicingParams, ServicingTypes } from 'app/models/servicing-params'
import { createReducer } from 'app/state/utils'
import {
  acceptedIds,
  invalidate,
  oneId,
  outdated,
  withRows,
} from 'app/state/utils/generation'
import {
  isServicingMutation,
  servicingWriteOf,
  staysWhenApproved,
} from './mutations'
import * as types from './types'
import {
  ServicingFeeAmountsResponse,
  ServicingFeedQuery,
  ServicingFeedResponse,
  ServicingFeedWindow,
} from './types'

export interface ServicingState {
  rows: ServicingParams[]
  /** The business date the feed was built for (YYYY-MM-DD); null before the first read. */
  asOf: string | null
  window: ServicingFeedWindow | null
  /** Reads in flight; the grid shows its loader while any is. */
  loading: number
  /** Message of the last failed read; cleared by the next success. */
  loadError: string | null
  /** The query of the last successful read — what a stale store reloads with. */
  lastQuery: ServicingFeedQuery | null
  /**
   * The window of the most recently STARTED read: the one the page wants.
   * A response for another window (My Work changes `to` when the business
   * date arrives or a range is picked) is dropped even if it lands last.
   */
  requested: ServicingFeedQuery | null
  /**
   * True once a write has happened that the rows cannot be patched for, or a
   * read came back from before a write. The page reloads once while on screen.
   */
  stale: boolean
  /**
   * Bumped by every write the store learns of. A request records the
   * generation it started under; a response from an older generation is
   * thrown away, because it predates a write (utils/generation.ts).
   */
  generation: number
  /** The fee-amounts read: fee rows show `amountPending` until it lands. */
  pricing: 'idle' | 'loading' | 'done' | 'failed'
  /** Which read the rows came from: the pricing stamped with another one is for rows no longer here. */
  readId: number | null
  /**
   * The writes applied to the rows that a read may not reflect yet. PQS
   * projects a write a moment after the ledger accepts it, so a read fired
   * right after a submit or a delete can still carry the old contract; each
   * of these is applied over every read until one agrees with it (or until
   * a few reads have gone by — a contract recreated by someone else is not
   * this write's ghost to hide). No timer: the reload runs at once.
   */
  pending: PendingWrite[]
}

export interface PendingWrite {
  id: string
  types: readonly ServicingTypes[]
  verb: 'submit' | 'reject' | 'approve' | 'delete'
  /** Reads this write has been re-applied over; retired after PENDING_WRITE_READS. */
  reads: number
}

/** How many reads a write is held over before the ledger is trusted to have moved on. */
export const PENDING_WRITE_READS = 3

export const initialServicingState: ServicingState = {
  rows: [],
  asOf: null,
  window: null,
  loading: 0,
  loadError: null,
  lastQuery: null,
  requested: null,
  stale: false,
  generation: 0,
  pricing: 'idle',
  readId: null,
  pending: [],
}

/** Two queries ask for the same window when their upper bounds agree. */
const sameWindow = (a: ServicingFeedQuery, b: ServicingFeedQuery) =>
  (a.to ?? null) === (b.to ?? null)

/**
 * The priced amounts onto the fee rows. A fee the ledger could not price, or
 * priced at 0, is absent from the answer and is not due: its row goes.
 */
const withFeeAmounts = (
  rows: ServicingParams[],
  amounts: Record<string, number>,
  unpriced: string[],
): ServicingParams[] => {
  const unknown = new Set(unpriced)
  return rows.flatMap(row => {
    if (row.type !== ServicingTypes.fee || !row.amountPending) return [row]
    // the ledger could not price it: the amount is unknown, the row stays
    if (unknown.has(row.id)) return [{ ...row, amountUnavailable: true }]
    const amount = amounts[row.id]
    if (amount === undefined) return []
    return [{ ...row, amount, amountPending: false }]
  })
}

const servicingReducer = createReducer(initialServicingState)({
  [types.GET_SERVICING]: (
    state: ServicingState,
    action: {
      payload: {
        response: ServicingFeedResponse
        query: ServicingFeedQuery
        generation?: number
        readId?: number
      }
    },
  ) => {
    const { response, query, readId } = action.payload
    // for a window the page no longer wants: dropped, nothing to retry
    if (state.requested && !sameWindow(query, state.requested)) return state
    // Fetched before a write the store has since learnt of: these rows would
    // undo it. Drop them and leave the store stale; the thunk reads once more.
    if (outdated(state, action.payload.generation))
      return { ...state, stale: true }
    return {
      ...state,
      ...overlaid(response.events ?? [], state.pending),
      asOf: response.asOf ?? null,
      window: response.window ?? null,
      loadError: null,
      lastQuery: query ?? {},
      stale: false,
      pricing: 'loading',
      readId: readId ?? null,
    }
  },
  [types.SERVICING_WINDOW_REQUESTED]: (
    state: ServicingState,
    action: { payload: ServicingFeedQuery },
  ) => ({
    ...state,
    requested: action.payload ?? {},
  }),
  [types.START_LOADING_SERVICING]: (state: ServicingState) => ({
    ...state,
    loading: state.loading + 1,
  }),
  [types.STOP_LOADING_SERVICING]: (state: ServicingState) => ({
    ...state,
    loading: Math.max(0, state.loading - 1),
  }),
  [types.SERVICING_LOAD_FAILED]: (
    state: ServicingState,
    action: {
      payload: {
        message: string
        query: ServicingFeedQuery
        generation?: number
      }
    },
  ) => {
    const { message, query, generation } = action.payload
    // a window the page no longer wants: nothing to report
    if (state.requested && !sameWindow(query, state.requested)) return state
    return {
      ...state,
      // a write landed during this read: its reload is still owed
      stale: outdated(state, generation) ? state.stale : false,
      loadError: message ?? 'Servicing feed request failed',
    }
  },
  [types.INVALIDATE_SERVICING]: invalidate,
  [types.SET_SERVICING_FEE_AMOUNTS]: (
    state: ServicingState,
    action: {
      payload: { response: ServicingFeeAmountsResponse; readId: number }
    },
  ) => {
    const { response, readId } = action.payload
    // priced for rows another read has since replaced: that read prices its own
    if (readId !== state.readId) return state
    const unpriced = response.unpriced ?? []
    return {
      ...state,
      rows: withFeeAmounts(state.rows, response.amounts ?? {}, unpriced),
      pricing: unpriced.length ? 'failed' : 'done',
    }
  },
  [types.SERVICING_FEE_AMOUNTS_FAILED]: (
    state: ServicingState,
    action: { payload: { message: string; readId: number } },
  ) => {
    if (action.payload.readId !== state.readId) return state
    return {
      ...state,
      rows: state.rows.map(row =>
        row.type === ServicingTypes.fee && row.amountPending
          ? { ...row, amountUnavailable: true }
          : row,
      ),
      pricing: 'failed',
    }
  },
})

/** The ids a write's answer names: one record (submit / approve / reject / delete of one) or a per-id list. */
const idsOf = (action: { payload?: any }): Set<string> =>
  new Set(
    Array.isArray(action.payload) ? acceptedIds(action.payload) : oneId(action),
  )

/** The row's stage and its `original.status` follow the ledger: on the feed a draft's stage IS its status. */
const withStage = (row: ServicingParams, status: string): ServicingParams => ({
  ...row,
  stage: status,
  original: { ...(row.original ?? {}), status },
})

/**
 * Every row on the feed is a contract some other duck writes; those writes
 * are dispatched by the row menus, the details pages and the Create New
 * stepper. The feed's store listens for them: a status flip or a delete is
 * applied to the row from the answer (never refetched — PQS lag), anything
 * else leaves the store stale so the page reloads once.
 */
/** The stage a pending write leaves its row in; none for a delete. */
const stageOf = (write: PendingWrite): string | undefined =>
  write.verb === 'submit'
    ? 'Submitted'
    : write.verb === 'reject'
      ? 'Draft'
      : write.verb === 'approve'
        ? 'Approved'
        : undefined

const removes = (write: PendingWrite, type: ServicingTypes): boolean =>
  write.verb === 'delete' ||
  (write.verb === 'approve' && !staysWhenApproved(type))

/** One pending write applied to one row it names: the row as the write left it, or none. */
const applied = (
  row: ServicingParams,
  write: PendingWrite,
): ServicingParams[] =>
  removes(write, row.type) ? [] : [withStage(row, stageOf(write) as string)]

/** Whether a fresh read already shows what the write did, so it can be retired. */
const reflected = (write: PendingWrite, fresh: ServicingParams[]): boolean => {
  const rows = fresh.filter(
    row => write.types.includes(row.type) && row.id === write.id,
  )
  if (rows.length === 0) return true // gone — a delete, or an approval that leaves the feed
  if (write.verb === 'delete') return false
  const stage = stageOf(write)
  return rows.every(row => row.stage === stage)
}

/**
 * A read's rows with the pending writes laid over them, and the writes that
 * are still pending after it: the ones the read did not reflect, that have
 * not yet been held over PENDING_WRITE_READS reads.
 */
const overlaid = (
  fresh: ServicingParams[],
  pending: PendingWrite[],
): { rows: ServicingParams[]; pending: PendingWrite[] } => {
  if (pending.length === 0) return { rows: fresh, pending }
  const stillPending = pending
    .filter(write => !reflected(write, fresh))
    .map(write => ({ ...write, reads: write.reads + 1 }))
    .filter(write => write.reads < PENDING_WRITE_READS)
  const rows = fresh.flatMap(row => {
    const over = stillPending.filter(
      write => write.types.includes(row.type) && write.id === row.id,
    )
    return over.reduce<ServicingParams[]>(
      (rows, write) => rows.flatMap(r => applied(r, write)),
      [row],
    )
  })
  return { rows, pending: stillPending }
}

/**
 * Every row on the feed is a contract some other duck writes; those writes
 * are dispatched by the row menus, the details pages and the Create New
 * stepper. The feed's store listens for them: a status flip or a delete is
 * applied to the row from the answer and remembered (`pending`) so a read
 * that predates its projection cannot undo it; a delete or an approval also
 * leaves the store stale, for the rows they derive (a deleted rollover makes
 * its loan a Maturing Loan again); anything else only leaves the store stale.
 */
const applyWrite = (
  state: ServicingState,
  action: { type: string; payload?: any },
): ServicingState => {
  const write = servicingWriteOf(action.type)
  if (write.verb === 'other') return invalidate(state)
  const ids = idsOf(action)
  if (ids.size === 0) return invalidate(state)
  const writes: PendingWrite[] = [...ids].map(id => ({
    id,
    types: write.types,
    verb: write.verb,
    reads: 0,
  }))
  const hit = (row: ServicingParams) =>
    write.types.includes(row.type) && ids.has(row.id)
  const patched = withRows(
    state,
    state.rows.flatMap(row => (hit(row) ? applied(row, writes[0]) : [row])),
  )
  const remembered = { ...patched, pending: [...state.pending, ...writes] }
  return write.verb === 'delete' || write.verb === 'approve'
    ? invalidate(remembered)
    : remembered
}

const servicingReducerWithMutations = (
  state: ServicingState = initialServicingState,
  action: { type: string; payload?: any },
): ServicingState => {
  const next = servicingReducer(state, action)
  return next === state && isServicingMutation(action.type)
    ? applyWrite(state, action)
    : next
}

export default servicingReducerWithMutations
